import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { rotaDaNotificacao } from '../utils/rotaDaNotificacao';

export const tempoRelativo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `há ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

/** Sino de notificações do topo (admin) — usa /notificacoes-admin. */
const NotificacoesDropdown = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notificacoes-admin');
      setNotifs(r.data.notificacoes || []);
      setNaoLidas(r.data.nao_lidas || 0);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    carregar();
    // Pausa o polling com a aba oculta (economiza requisições).
    const t = setInterval(() => { if (!document.hidden) carregar(); }, 30000);
    return () => clearInterval(t);
  }, [carregar]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = () => { const novo = !open; setOpen(novo); if (novo) carregar(); };

  const marcarLida = async (id) => {
    try {
      await api.post(`/notificacoes-admin/${encodeURIComponent(id)}/ler`, {});
      setNotifs((p) => p.map((n) => (n.id === id ? { ...n, lida: true } : n)));
      setNaoLidas((p) => Math.max(0, p - 1));
    } catch { /* silencioso */ }
  };

  const marcarTodas = async () => {
    try {
      await api.post('/notificacoes-admin/ler-todas', {});
      setNotifs((p) => p.map((n) => ({ ...n, lida: true })));
      setNaoLidas(0);
    } catch { /* silencioso */ }
  };

  // Destino ao clicar — a régua única de `utils/rotaDaNotificacao` (menção na Esteira, tarefa).
  const rotaDaNotif = rotaDaNotificacao;

  const abrirNotif = (n) => {
    if (!n.lida) marcarLida(n.id);
    const rota = rotaDaNotif(n);
    if (rota) { setOpen(false); navigate(rota); }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        data-testid="notif-bell"
        className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 transition-colors relative"
        title="Notificações"
      >
        <Bell className="w-4 h-4" />
        {naoLidas > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-2xs font-bold flex items-center justify-center">
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-800">Notificações</span>
            {naoLidas > 0 && (
              <button onClick={marcarTodas} className="text-xs text-primary hover:underline flex items-center gap-1">
                <Check className="w-3 h-3" /> Marcar todas
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
            ) : notifs.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">Nenhuma notificação</div>
            ) : (
              notifs.slice(0, 8).map((n) => (
                <button
                  key={n.id}
                  onClick={() => abrirNotif(n)}
                  className={`w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${n.lida ? '' : 'bg-blue-50/40'}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.lida && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                    <div className={`min-w-0 flex-1 ${n.lida ? 'pl-3.5' : ''}`}>
                      <p className="text-sm font-medium text-slate-800 truncate">{n.titulo}</p>
                      {n.mensagem && <p className="text-xs text-slate-500 line-clamp-2">{n.mensagem}</p>}
                      <p className="text-xxs text-slate-400 mt-0.5">{tempoRelativo(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); navigate('/notificacoes'); }}
            className="w-full px-4 py-2.5 text-sm text-center text-primary font-medium hover:bg-slate-50 border-t border-slate-100"
          >
            Ver todas as notificações
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificacoesDropdown;
